import { useState, useCallback } from 'react';
import { piecesService } from '../services/api';
import { Piece, OCRResult } from '../types/api';

interface UsePiecesReturn {
  pieces: Piece[];
  isLoading: boolean;
  error: string | null;
  loadPieces: (courrierId: number) => Promise<void>;
  downloadPiece: (pieceId: number, filename?: string) => Promise<void>;
  getOCRText: (pieceId: number) => Promise<OCRResult>;
  triggerOCR: (pieceId: number) => Promise<{ jobId: string }>;
  validateOCR: (pieceId: number, validatedData: {
    text?: string;
    extracted_metadata?: Record<string, any>;
    confidence?: number;
  }) => Promise<OCRResult>;
  deletePiece: (pieceId: number) => Promise<void>;
  updatePiece: (pieceId: number, updates: {
    filename?: string;
    is_primary?: boolean;
  }) => Promise<Piece>;
  reset: () => void;
}

export function usePieces(): UsePiecesReturn {
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPieces = useCallback(async (courrierId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const piecesData = await piecesService.getPieces(courrierId);
      setPieces(piecesData);
    } catch (err: any) {
      console.error('Erreur lors du chargement des pièces jointes:', err);
      setError(err.message || 'Erreur lors du chargement des pièces jointes');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadPiece = useCallback(async (pieceId: number, filename?: string) => {
    try {
      setError(null);
      // Si aucun nom de fichier n'est fourni, essayer de trouver la pièce dans la liste pour utiliser son nom original
      if (!filename) {
        const piece = pieces.find(p => p.id === pieceId);
        if (piece) {
          filename = piece.original_name || piece.filename;
        }
      }
      await piecesService.downloadPiece(pieceId, filename);
    } catch (err: any) {
      console.error('Erreur lors du téléchargement de la pièce jointe:', err);
      setError(err.message || 'Erreur lors du téléchargement');
      throw err;
    }
  }, [pieces]);

  const getOCRText = useCallback(async (pieceId: number): Promise<OCRResult> => {
    try {
      setError(null);
      return await piecesService.getOCRText(pieceId);
    } catch (err: any) {
      console.error('Erreur lors de la récupération du texte OCR:', err);
      setError(err.message || 'Erreur lors de la récupération du texte OCR');
      throw err;
    }
  }, []);

  const triggerOCR = useCallback(async (pieceId: number): Promise<{ jobId: string }> => {
    try {
      setError(null);
      return await piecesService.triggerOCR(pieceId);
    } catch (err: any) {
      console.error('Erreur lors du déclenchement de l\'OCR:', err);
      setError(err.message || 'Erreur lors du déclenchement de l\'OCR');
      throw err;
    }
  }, []);

  const validateOCR = useCallback(async (
    pieceId: number,
    validatedData: {
      text?: string;
      extracted_metadata?: Record<string, any>;
      confidence?: number;
    },
  ): Promise<OCRResult> => {
    try {
      setError(null);
      return await piecesService.validateOCR(pieceId, validatedData);
    } catch (err: any) {
      console.error('Erreur lors de la validation de l\'OCR:', err);
      setError(err.message || 'Erreur lors de la validation de l\'OCR');
      throw err;
    }
  }, []);

  const deletePiece = useCallback(async (pieceId: number): Promise<void> => {
    try {
      setError(null);
      await piecesService.deletePiece(pieceId);

      // Supprimer la pièce de la liste locale
      setPieces(prev => prev.filter(piece => piece.id !== pieceId));
    } catch (err: any) {
      console.error('Erreur lors de la suppression de la pièce jointe:', err);
      setError(err.message || 'Erreur lors de la suppression');
      throw err;
    }
  }, []);

  const updatePiece = useCallback(async (
    pieceId: number,
    updates: {
      filename?: string;
      is_primary?: boolean;
    },
  ): Promise<Piece> => {
    try {
      setError(null);
      const updatedPiece = await piecesService.updatePiece(pieceId, updates);

      // Mettre à jour la pièce dans la liste locale
      setPieces(prev =>
        prev.map(piece =>
          piece.id === pieceId ? updatedPiece : piece
        )
      );

      return updatedPiece;
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour de la pièce jointe:', err);
      setError(err.message || 'Erreur lors de la mise à jour');
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setPieces([]);
    setError(null);
  }, []);

  return {
    pieces,
    isLoading,
    error,
    loadPieces,
    downloadPiece,
    getOCRText,
    triggerOCR,
    validateOCR,
    deletePiece,
    updatePiece,
    reset,
  };
}